import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Check, Smartphone, Plus, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useQuoteCart } from '../context/QuoteCartContext';
import { productsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';

export function ProductPage() {
  const { slug } = useParams();
  const { t, language } = useLanguage();
  const { addItem } = useQuoteCart();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedNFC, setSelectedNFC] = useState(null);
  const [selectedPrinting, setSelectedPrinting] = useState(null);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    loadProduct();
  }, [slug]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const res = await productsAPI.getBySlug(slug);
      setProduct(res.data);
      if (res.data.variants?.length > 0) {
        setSelectedVariant(res.data.variants[0].variant_id);
      }
      if (res.data.nfc_options?.length > 0) {
        setSelectedNFC(0);
      }
      if (res.data.printing_options?.length > 0) {
        setSelectedPrinting(res.data.printing_options[0].option_id);
      }
      if (res.data.min_quantity) {
        setQuantity(res.data.min_quantity);
      }
    } catch (error) {
      console.error('Failed to load product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToQuote = () => {
    const variant = product.variants?.find(v => v.variant_id === selectedVariant);
    const nfcOption = selectedNFC !== null ? product.nfc_options[selectedNFC] : null;
    
    addItem(product, {
      variant_id: selectedVariant,
      variant_name: variant ? (language === 'fr' ? variant.name_fr : variant.name_en) : null,
      quantity,
      nfc_option: nfcOption,
      printing_option: selectedPrinting,
    });
    
    toast.success(language === 'fr' ? 'Produit ajouté à votre demande de devis' : 'Product added to your quote request');
  };

  const handleRequestSample = () => {
    navigate('/demande-echantillon', { state: { product } });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          <Skeleton className="aspect-square" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-12 w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 text-center">
        <p className="text-muted-foreground mb-4">
          {language === 'fr' ? 'Produit non trouvé' : 'Product not found'}
        </p>
        <Link to="/catalogue">
          <Button variant="outline">{t('common.back')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-page">
      {/* Breadcrumb */}
      <div className="bg-secondary border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Link to="/" className="text-muted-foreground hover:text-[#1F2937]">
              {t('nav.home')}
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <Link to="/catalogue" className="text-muted-foreground hover:text-[#1F2937]">
              {t('nav.catalog')}
            </Link>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <span className="text-[#1F2937] line-clamp-1">
              {language === 'fr' ? product.name_fr : product.name_en}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-secondary border border-border overflow-hidden">
              {product.images?.[selectedImage] ? (
                <img
                  src={product.images[selectedImage]}
                  alt={language === 'fr' ? product.name_fr : product.name_en}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground text-2xl font-mono">
                  NFC
                </div>
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-4">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 border ${selectedImage === index ? 'border-[#1F2937]' : 'border-border'} overflow-hidden`}
                    data-testid={`product-thumbnail-${index}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']" data-testid="product-title">
                {language === 'fr' ? product.name_fr : product.name_en}
              </h1>
              <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
                {language === 'fr' ? product.description_fr : product.description_en}
              </p>
            </div>

            {/* Quick Info */}
            <div className="flex flex-wrap gap-4">
              {product.usage_indoor && (
                <span className="px-3 py-1 bg-secondary text-sm">
                  {t('products.indoor')}
                </span>
              )}
              {product.usage_outdoor && (
                <span className="px-3 py-1 bg-secondary text-sm">
                  {t('products.outdoor')}
                </span>
              )}
              <span className="px-3 py-1 bg-secondary text-sm font-mono">
                Min: {product.min_quantity} {language === 'fr' ? 'unités' : 'units'}
              </span>
            </div>

            <Tabs defaultValue="variants" className="w-full">
              <TabsList className="w-full justify-start rounded-none border-b border-border bg-transparent p-0">
                {product.variants?.length > 0 && (
                  <TabsTrigger
                    value="variants"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#1F2937] data-[state=active]:bg-transparent"
                  >
                    {t('products.variants')}
                  </TabsTrigger>
                )}
                {product.nfc_options?.length > 0 && (
                  <TabsTrigger
                    value="nfc"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#1F2937] data-[state=active]:bg-transparent"
                  >
                    {t('products.nfcOptions')}
                  </TabsTrigger>
                )}
                {product.printing_options?.length > 0 && (
                  <TabsTrigger
                    value="printing"
                    className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-[#1F2937] data-[state=active]:bg-transparent"
                  >
                    {t('products.printingOptions')}
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Variants Tab */}
              {product.variants?.length > 0 && (
                <TabsContent value="variants" className="pt-6">
                  <RadioGroup value={selectedVariant} onValueChange={setSelectedVariant}>
                    <div className="grid grid-cols-2 gap-4">
                      {product.variants.map((variant) => (
                        <div key={variant.variant_id}>
                          <RadioGroupItem
                            value={variant.variant_id}
                            id={variant.variant_id}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={variant.variant_id}
                            className="flex items-center justify-between p-4 border border-border cursor-pointer peer-data-[state=checked]:border-[#1F2937] peer-data-[state=checked]:bg-secondary hover:border-[#1F2937]/50 transition-colors"
                            data-testid={`variant-${variant.variant_id}`}
                          >
                            <span>{language === 'fr' ? variant.name_fr : variant.name_en}</span>
                            {selectedVariant === variant.variant_id && (
                              <Check className="h-4 w-4 text-[#DC2626]" />
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </TabsContent>
              )}

              {/* NFC Options Tab */}
              {product.nfc_options?.length > 0 && (
                <TabsContent value="nfc" className="pt-6">
                  <div className="space-y-4">
                    {product.nfc_options.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedNFC(index)}
                        className={`w-full p-4 text-left border ${selectedNFC === index ? 'border-[#1F2937] bg-secondary' : 'border-border'} hover:border-[#1F2937]/50 transition-colors`}
                        data-testid={`nfc-option-${index}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="space-y-2">
                            <p className="font-semibold font-mono">{option.chip_type}</p>
                            <p className="text-sm text-muted-foreground">{t('products.capacity')}: {option.capacity}</p>
                            <div className="flex gap-2">
                              {option.ios_compatible && (
                                <span className="text-xs px-2 py-1 bg-white border border-border flex items-center gap-1">
                                  <Smartphone className="h-3 w-3" /> iOS
                                </span>
                              )}
                              {option.android_compatible && (
                                <span className="text-xs px-2 py-1 bg-white border border-border flex items-center gap-1">
                                  <Smartphone className="h-3 w-3" /> Android
                                </span>
                              )}
                            </div>
                          </div>
                          {selectedNFC === index && (
                            <Check className="h-5 w-5 text-[#DC2626]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </TabsContent>
              )}

              {/* Printing Options Tab */}
              {product.printing_options?.length > 0 && (
                <TabsContent value="printing" className="pt-6">
                  <RadioGroup value={selectedPrinting} onValueChange={setSelectedPrinting}>
                    <div className="space-y-3">
                      {product.printing_options.map((option) => (
                        <div key={option.option_id}>
                          <RadioGroupItem
                            value={option.option_id}
                            id={option.option_id}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={option.option_id}
                            className="flex items-center justify-between p-4 border border-border cursor-pointer peer-data-[state=checked]:border-[#1F2937] peer-data-[state=checked]:bg-secondary hover:border-[#1F2937]/50 transition-colors"
                            data-testid={`printing-${option.option_id}`}
                          >
                            <div>
                              <span className="font-medium">{language === 'fr' ? option.name_fr : option.name_en}</span>
                              <span className="text-sm text-muted-foreground ml-2">
                                ({option.colors === 5 ? 'CMYK' : `${option.colors} ${t('products.colors')}`})
                              </span>
                            </div>
                            {selectedPrinting === option.option_id && (
                              <Check className="h-4 w-4 text-[#DC2626]" />
                            )}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                </TabsContent>
              )}
            </Tabs>

            {/* Quantity */}
            <div>
              <Label className="text-sm font-medium mb-2 block">{t('quote.quantity')}</Label>
              <Input
                type="number"
                min={product.min_quantity || 1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(product.min_quantity || 1, parseInt(e.target.value) || 1))}
                className="w-32 rounded-none"
                data-testid="quantity-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('products.minQuantity')}: {product.min_quantity || 1}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={handleAddToQuote}
                className="flex-1 bg-[#1F2937] hover:bg-[#1F2937]/90 rounded-none uppercase tracking-widest text-xs py-6 h-auto"
                data-testid="add-to-quote-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('products.requestQuote')}
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={handleRequestSample}
                className="flex-1 rounded-none uppercase tracking-widest text-xs py-6 h-auto border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white"
                data-testid="request-sample-btn"
              >
                {t('products.requestSample')}
              </Button>
            </div>

            {/* Materials */}
            {product.materials?.length > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2">{t('products.materials')}</h4>
                <div className="flex flex-wrap gap-2">
                  {product.materials.map((mat) => (
                    <span key={mat} className="px-3 py-1 bg-secondary text-sm font-mono">
                      {mat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductPage;
